import clsx from 'clsx';
import type { CSSProperties, PointerEventHandler, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './ResizablePanes.module.scss';
import { Separator } from './Separator';

const DEFAULT_MIN_WIDTH = 50;
const DEFAULT_SEPARATOR_WIDTH = 8;

type ResizablePanesProps = {
  left: ReactNode;
  right: ReactNode;
  leftVisible: boolean;
  rightVisible: boolean;
  rightRatio: number;
  onRightRatioChange: (value: number) => void;
  minLeftWidth?: number;
  minRightWidth?: number;
  separatorWidth?: number;
};

function getRightPaneRatio(params: {
  pointerX: number;
  containerLeft: number;
  containerWidth: number;
  minLeftWidth: number;
  minRightWidth: number;
  separatorWidth: number;
}) {
  const availableWidth = params.containerWidth - params.separatorWidth;
  if (availableWidth <= 0) {
    return 0.5;
  }

  const rawRightWidth = params.containerLeft + params.containerWidth - params.pointerX;
  const rawRatio = rawRightWidth / availableWidth;

  return clampRightPaneRatio({
    value: rawRatio,
    containerWidth: params.containerWidth,
    minLeftWidth: params.minLeftWidth,
    minRightWidth: params.minRightWidth,
    separatorWidth: params.separatorWidth,
  });
}

function clampRightPaneRatio(params: {
  value: number;
  containerWidth: number;
  minLeftWidth: number;
  minRightWidth: number;
  separatorWidth: number;
}) {
  if (!Number.isFinite(params.value)) {
    return 0.5;
  }

  const availableWidth = params.containerWidth - params.separatorWidth;
  if (availableWidth <= 0) {
    return 0.5;
  }

  const minRightRatio = Math.min(params.minRightWidth / availableWidth, 1);
  const maxRightRatio = Math.max(1 - params.minLeftWidth / availableWidth, 0);
  const minRatio = Math.min(minRightRatio, maxRightRatio);
  const maxRatio = Math.max(minRightRatio, maxRightRatio);

  return Math.min(Math.max(params.value, minRatio), maxRatio);
}

function getPaneStyle(ratio: number, isSplitVisible: boolean, separatorWidth: number): CSSProperties {
  if (!isSplitVisible) {
    return { flex: '1 1 100%' };
  }

  return {
    flex: `0 0 calc((100% - ${separatorWidth}px) * ${ratio})`,
  };
}

// eslint-disable-next-line mobx/missing-observer
export function ResizablePanes({
  left,
  right,
  leftVisible,
  rightVisible,
  rightRatio,
  onRightRatioChange,
  minLeftWidth = DEFAULT_MIN_WIDTH,
  minRightWidth = DEFAULT_MIN_WIDTH,
  separatorWidth = DEFAULT_SEPARATOR_WIDTH,
}: ResizablePanesProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isSplitVisible = leftVisible && rightVisible;
  const [isDragging, setIsDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const updateContainerWidth = () => {
      setContainerWidth(container.getBoundingClientRect().width);
    };

    updateContainerWidth();

    const resizeObserver = new ResizeObserver(updateContainerWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  const clampedRightRatio = isSplitVisible
    ? clampRightPaneRatio({
        value: rightRatio,
        containerWidth,
        minLeftWidth,
        minRightWidth,
        separatorWidth,
      })
    : rightRatio;

  const handleSeparatorPointerDown = useCallback<PointerEventHandler<HTMLDivElement>>(
    (event) => {
      event.preventDefault();
      setIsDragging(true);
      const handlePointerMove = (event: PointerEvent) => {
        const rect = containerRef.current?.getBoundingClientRect();

        if (!rect) {
          return;
        }

        onRightRatioChange(
          getRightPaneRatio({
            pointerX: event.clientX,
            containerLeft: rect.left,
            containerWidth: rect.width,
            minLeftWidth,
            minRightWidth,
            separatorWidth,
          }),
        );
      };

      const handlePointerUp = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', handlePointerUp);
        setIsDragging(false);
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);
    },
    [minLeftWidth, minRightWidth, onRightRatioChange, separatorWidth],
  );

  return (
    <div className={clsx(styles.ResizablePanes, isDragging && styles.isDragging)} ref={containerRef}>
      {leftVisible && (
        <div className={styles.Pane} style={getPaneStyle(1 - clampedRightRatio, isSplitVisible, separatorWidth)}>
          {left}
        </div>
      )}
      {isSplitVisible && <Separator onPointerDown={handleSeparatorPointerDown} />}
      {rightVisible && (
        <div className={styles.Pane} style={getPaneStyle(clampedRightRatio, isSplitVisible, separatorWidth)}>
          {right}
        </div>
      )}
    </div>
  );
}
